import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { CategoryId } from '#server/models';
import { VIEWER_TYPES, ViewerType } from '#server/models';
import { Cache } from 'three';
import { saveDraftFile, getDraftFile, deleteDraftFiles } from '@/lib/adminDraftFiles';

export interface AxesLabelsDraft {
  x1: string;
  x2: string;
  y1: string;
  y2: string;
  z1: string;
  z2: string;
}

export interface LabelDraft {
  _id: string;
  /** DB id — undefined for new labels, set when editing an existing subject. */
  id?: number;
  title: string;
  slug: string;
  description: string;
  primaryCategoryId: CategoryId | null;
  categoryIds: CategoryId[];
  unitIds: number[];
  config3D: {
    position: [number, number, number];
    camera: {
      rotation: { azimuthAngle: number; polarAngle: number };
      position: [number, number, number];
      focalOffset: [number, number, number];
      distance: number;
    };
  };
}

export interface SubjectDraft {
  /** DB id — undefined for new subjects, set when editing an existing subject. */
  id?: number;
  // Step 1
  title: string;
  slug: string;
  type: ViewerType;
  disciplineId: number | null;
  description: string;
  thumbnailPath: string;
  unitIds: number[];

  // Step 2 – 3D config
  config3D: {
    modelPath: string;
    texturePath: string;
    contextModelPath: string;
    referenceContextModelPath: string;
    // Blob URLs for preview (not persisted to sessionStorage)
    _modelBlobUrl?: string;
    _textureBlobUrl?: string;
    _contextBlobUrl?: string;
    _referenceContextBlobUrl?: string;
    scale: [number, number, number];
    position: [number, number, number];
    rotation: [number, number, number];
    distMin: number;
    distMax: number;
    dist: number;
    ambientLight: number;
    axes: boolean;
    axesLabels: AxesLabelsDraft;
    labelVisibleDist: number;
    labelVisibleRange: number;
    context: {
      subjectContextEnabled: boolean;
      subjectContext: {
        position: [number, number, number];
        rotation: [number, number, number];
        scale: [number, number, number];
      };
      referenceContext: {
        position: [number, number, number];
        rotation: [number, number, number];
        scale: [number, number, number];
      };
      fullContext: {
        rotation: [number, number, number];
      };
    };
  };

  // Step 3 – categories & units
  categoryIds: CategoryId[];

  // Step 4 – labels
  labels: LabelDraft[];

  currentStep: number;
  highestStepReached: number;
}

export const defaultDraft: SubjectDraft = {
  title: '',
  slug: '',
  type: '3D',
  disciplineId: null,
  description: '',
  thumbnailPath: 'medical/thumbnails/placeholder.webp',
  unitIds: [],
  config3D: {
    modelPath: '',
    texturePath: '',
    contextModelPath: '',
    referenceContextModelPath: '',
    scale: [1, 1, 1],
    position: [0, 0, 0],
    rotation: [0, 0, 0],
    distMin: 5,
    distMax: 500,
    dist: 100,
    ambientLight: 1,
    axes: true,
    axesLabels: { x1: 'Left', x2: 'Right', y1: 'Superior', y2: 'Inferior', z1: 'Anterior', z2: 'Posterior' },
    labelVisibleDist: 1200,
    labelVisibleRange: 0,
    context: {
      subjectContextEnabled: false,
      subjectContext: { position: [0, 0, 0], rotation: [0, 0, 0], scale: [0.032, 0.032, 0.032] },
      referenceContext: { position: [0, 0, 0], rotation: [0, 0, 0], scale: [0.032, 0.032, 0.032] },
      fullContext: { rotation: [0, 0, 0] }
    }
  },
  categoryIds: [],
  labels: [],
  currentStep: 1,
  highestStepReached: 1
};

/** Returns a serialisable snapshot that excludes navigation state and blob URLs. */
function comparableDraft(draft: SubjectDraft) {
  const { currentStep, highestStepReached, config3D, ...rest } = draft;
  const { _modelBlobUrl, _textureBlobUrl, _contextBlobUrl, _referenceContextBlobUrl, ...config3DRest } = config3D;
  return { ...rest, config3D: config3DRest };
}

function stripBlobUrls(draft: SubjectDraft): SubjectDraft {
  const { config3D, ...rest } = draft;
  const { _modelBlobUrl, _textureBlobUrl, _contextBlobUrl, _referenceContextBlobUrl, ...config3DRest } = config3D;
  return { ...rest, config3D: config3DRest };
}

type BlobField = '_modelBlobUrl' | '_textureBlobUrl' | '_contextBlobUrl' | '_referenceContextBlobUrl';

const BLOB_FIELD_CONFIG: Record<BlobField, { suffix: string; type: 'fbx' | 'image' }> = {
  _modelBlobUrl: { suffix: 'model', type: 'fbx' },
  _textureBlobUrl: { suffix: 'texture', type: 'image' },
  _contextBlobUrl: { suffix: 'context', type: 'fbx' },
  _referenceContextBlobUrl: { suffix: 'refcontext', type: 'fbx' },
};

/**
 * Creates a blob URL for preview and pre-populates THREE.Cache so Three.js never
 * needs to fetch() the blob URL (which CSP connect-src/img-src can block).
 * - FBX: caches the raw ArrayBuffer (what FileLoader expects)
 * - Image: caches an ImageBitmap (what ImageLoader/TextureLoader expects)
 */
async function createCachedBlobUrl(file: File, type: 'fbx' | 'image'): Promise<string> {
  const blobUrl = URL.createObjectURL(file);
  if (type === 'fbx') {
    const arrayBuffer = await file.arrayBuffer();
    Cache.add(blobUrl, arrayBuffer);
  } else {
    const bitmap = await createImageBitmap(file);
    Cache.add(blobUrl, bitmap);
  }
  return blobUrl;
}

interface AdminSubjectDraftContextValue {
  draft: SubjectDraft;
  updateDraft: (patch: Partial<SubjectDraft>) => void;
  /** Convenience wrapper: merges a partial patch into `draft.config3D`. */
  updateConfig: (patch: Partial<SubjectDraft['config3D']>) => void;
  resetDraft: () => void;
  areaSlug: string;
  /** True when creating a new subject; false when editing an existing one. */
  isNew: boolean;
  /** The draft as it was when the wizard first loaded. Used for edit-mode SQL diffing. */
  initialDraft: SubjectDraft;
  /** Persist a file to IndexedDB so the preview survives a page refresh. */
  persistDraftFile: (field: BlobField, file: File) => void;
  /** True when the draft differs from the initial state (excluding navigation fields and blob URLs). */
  hasChanges: boolean;
}

const AdminSubjectDraftContext = createContext<AdminSubjectDraftContextValue | null>(null);

export const useAdminSubjectDraft = (): AdminSubjectDraftContextValue => {
  const ctx = useContext(AdminSubjectDraftContext);
  if (!ctx) throw new Error('useAdminSubjectDraft must be used within AdminSubjectDraftProvider');
  return ctx;
};

interface AdminSubjectDraftProviderProps {
  children: ReactNode;
  areaSlug: string;
  /** Unique key used for localStorage — typically `${areaSlug}-new` for create or the subjectSlug for edit. */
  draftKey: string;
  /** Explicitly marks create vs edit mode. Determines whether INSERT or UPDATE SQL is generated. */
  isNew: boolean;
  /** Pre-populated draft used when no localStorage data exists (edit flow). */
  initialDraft?: SubjectDraft;
}

export const AdminSubjectDraftProvider = ({
  children,
  areaSlug,
  draftKey,
  isNew,
  initialDraft,
}: AdminSubjectDraftProviderProps) => {
  const stableInitialDraft = initialDraft ?? defaultDraft;
  const storageKey = `admin-subject-draft-${draftKey}`;

  const loadFromStorage = (): SubjectDraft => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const parsed = JSON.parse(raw) as Partial<SubjectDraft>;
        // Deep-merge config3D separately so we can strip stale blob URL fields.
        // A shallow spread of the whole parsed object would replace config3D entirely,
        // preserving old blob URLs from sessions before stripBlobUrls was in place.
        const {
          _modelBlobUrl: _m, _textureBlobUrl: _t,
          _contextBlobUrl: _c, _referenceContextBlobUrl: _r,
          ...parsedConfig3D
        } = parsed.config3D ?? {};
        const stored: SubjectDraft = {
          ...defaultDraft,
          ...parsed,
          config3D: { ...defaultDraft.config3D, ...parsedConfig3D },
        };
        // Re-inject blob URLs from initialDraft if present (edit flow uses signed URLs).
        if (initialDraft) {
          const { _modelBlobUrl, _textureBlobUrl, _contextBlobUrl, _referenceContextBlobUrl } = initialDraft.config3D;
          stored.config3D = {
            ...stored.config3D,
            ...(_modelBlobUrl && { _modelBlobUrl }),
            ...(_textureBlobUrl && { _textureBlobUrl }),
            ...(_contextBlobUrl && { _contextBlobUrl }),
            ...(_referenceContextBlobUrl && { _referenceContextBlobUrl }),
          };
        }
        return stored;
      }
    } catch {
      // ignore parse errors
    }
    return initialDraft ?? defaultDraft;
  };

  const [draft, setDraft] = useState<SubjectDraft>(loadFromStorage);

  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify(stripBlobUrls(draft)));
  }, [draft, storageKey]);

  // On mount: restore blob URLs from IndexedDB and pre-populate THREE.Cache so
  // Three.js can load them without triggering a fetch() (which CSP may block).
  useEffect(() => {
    const fields = Object.entries(BLOB_FIELD_CONFIG) as [BlobField, { suffix: string; type: 'fbx' | 'image' }][];
    Promise.all(
      fields.map(async ([field, { suffix, type }]) => {
        const file = await getDraftFile(`${storageKey}-${suffix}`);
        if (!file) return { field, url: null };
        const url = await createCachedBlobUrl(file, type);
        return { field, url };
      })
    ).then((results) => {
      const patch: Partial<SubjectDraft['config3D']> = {};
      let hasAny = false;
      for (const { field, url } of results) {
        if (url) {
          patch[field] = url;
          hasAny = true;
        }
      }
      if (hasAny) {
        setDraft((prev) => ({ ...prev, config3D: { ...prev.config3D, ...patch } }));
      }
    }).catch(() => {});
  }, [storageKey]);

  const updateDraft = (patch: Partial<SubjectDraft>) => {
    setDraft((prev) => ({ ...prev, ...patch }));
  };

  const updateConfig = (patch: Partial<SubjectDraft['config3D']>) => {
    setDraft((prev) => ({ ...prev, config3D: { ...prev.config3D, ...patch } }));
  };

  const resetDraft = () => {
    localStorage.removeItem(storageKey);
    deleteDraftFiles(storageKey).catch(() => {});
    setDraft(initialDraft ?? defaultDraft);
  };

  const persistDraftFile = (field: BlobField, file: File) => {
    saveDraftFile(`${storageKey}-${BLOB_FIELD_CONFIG[field].suffix}`, file).catch(() => {});
  };

  const hasChanges =
    JSON.stringify(comparableDraft(draft)) !== JSON.stringify(comparableDraft(stableInitialDraft));

  return (
    <AdminSubjectDraftContext.Provider value={{ draft, updateDraft, updateConfig, resetDraft, areaSlug, isNew, initialDraft: stableInitialDraft, persistDraftFile, hasChanges }}>
      {children}
    </AdminSubjectDraftContext.Provider>
  );
};

export { AdminSubjectDraftContext };
export { VIEWER_TYPES };
